import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  FaShieldAlt, FaCheckCircle, FaExclamationTriangle, FaTimesCircle,
  FaUpload, FaFileAlt, FaIdCard, FaBuilding, FaCreditCard,
  FaSpinner, FaSearch, FaEye, FaTimes, FaCheck
} from 'react-icons/fa';

export default function SecurityDashboard() {
  const { i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  
  const [stats, setStats] = useState(null);
  const [pendingDocs, setPendingDocs] = useState([]);
  const [highRiskUsers, setHighRiskUsers] = useState([]);
  const [fraudReports, setFraudReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Document verification modal
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [verificationAction, setVerificationAction] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [statsRes, docsRes, riskRes, reportsRes] = await Promise.all([
        axios.get(`${API}/security/admin/security-stats?admin_id=${user.id}`),
        axios.get(`${API}/security/admin/pending-verifications?admin_id=${user.id}`),
        axios.get(`${API}/security/admin/high-risk-users?admin_id=${user.id}`),
        axios.get(`${API}/security/fraud-reports?status=pending`)
      ]);
      
      setStats(statsRes.data);
      setPendingDocs(docsRes.data);
      setHighRiskUsers(riskRes.data);
      setFraudReports(reportsRes.data);
    } catch (error) {
      console.error('Error fetching security data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyDocument = async (docId, status) => {
    try {
      await axios.put(
        `${API}/security/documents/${docId}/verify?admin_id=${user.id}&status=${status}${status === 'rejected' ? `&rejection_reason=${encodeURIComponent(rejectionReason)}` : ''}`
      );
      
      toast.success(status === 'verified' ? 'Document vérifié' : 'Document rejeté');
      setSelectedDoc(null);
      setRejectionReason('');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la vérification');
    }
  };

  const handleReviewFraudReport = async (reportId, status) => {
    try {
      await axios.put(
        `${API}/security/fraud-reports/${reportId}/review?admin_id=${user.id}&status=${status}`
      );
      
      toast.success(status === 'confirmed' ? 'Signalement confirmé' : 'Signalement rejeté');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la révision');
    }
  };

  const getRiskLevelBadge = (score) => {
    if (score <= 30) return <Badge className="bg-green-500">Faible</Badge>;
    if (score <= 60) return <Badge className="bg-orange-500">Moyen</Badge>;
    return <Badge className="bg-red-500">Élevé</Badge>;
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      kbis: 'Kbis',
      cni: "Carte d'identité",
      passport: 'Passeport',
      rib: 'RIB',
      driving_license: 'Permis de conduire',
      carte_grise: 'Carte grise'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <FaSpinner className="animate-spin text-4xl text-[#635BFF]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className="p-3 bg-[#635BFF] rounded-xl">
            <FaShieldAlt className="text-white text-2xl" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#0A2540]">Sécurité & Anti-Fraude</h1>
            <p className="text-gray-500">Gestion des vérifications et alertes</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <FaFileAlt className="text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0A2540]">{stats?.pending_documents || 0}</p>
                  <p className="text-sm text-gray-500">Documents en attente</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <FaCheckCircle className="text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0A2540]">{stats?.verified_users || 0}</p>
                  <p className="text-sm text-gray-500">Utilisateurs vérifiés</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <FaExclamationTriangle className="text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0A2540]">{stats?.high_risk_users || 0}</p>
                  <p className="text-sm text-gray-500">Utilisateurs à risque</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <FaBuilding className="text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#0A2540]">{stats?.valid_siren_verifications || 0}</p>
                  <p className="text-sm text-gray-500">SIREN vérifiés</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
            <TabsTrigger value="documents">
              Documents ({pendingDocs.length})
            </TabsTrigger>
            <TabsTrigger value="risk">
              Risques ({highRiskUsers.length})
            </TabsTrigger>
            <TabsTrigger value="reports">
              Signalements ({fraudReports.length})
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Recent Pending Documents */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FaFileAlt className="text-[#635BFF]" />
                    Documents en attente
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {pendingDocs.slice(0, 5).map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between py-3 border-b last:border-0">
                      <div>
                        <p className="font-medium">{doc.user?.name || 'Utilisateur'}</p>
                        <p className="text-sm text-gray-500">{getDocumentTypeLabel(doc.document_type)}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSelectedDoc(doc)}
                      >
                        <FaEye className="mr-1" /> Voir
                      </Button>
                    </div>
                  ))}
                  {pendingDocs.length === 0 && (
                    <p className="text-gray-500 text-center py-4">Aucun document en attente</p>
                  )}
                </CardContent>
              </Card>

              {/* High Risk Users */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FaExclamationTriangle className="text-red-500" />
                    Utilisateurs à risque
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {highRiskUsers.slice(0, 5).map((risk) => (
                    <div key={risk.id} className="flex items-center justify-between py-3 border-b last:border-0">
                      <div>
                        <p className="font-medium">{risk.user?.name || 'Utilisateur'}</p>
                        <p className="text-sm text-gray-500">{risk.user?.email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-red-500">{risk.score}</span>
                        {getRiskLevelBadge(risk.score)}
                      </div>
                    </div>
                  ))}
                  {highRiskUsers.length === 0 && (
                    <p className="text-gray-500 text-center py-4">Aucun utilisateur à risque</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <Card>
              <CardContent className="p-6">
                <div className="space-y-4">
                  {pendingDocs.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between p-4 bg-white border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-gray-100 rounded-lg">
                          {doc.document_type === 'kbis' && <FaBuilding className="text-gray-600" />}
                          {doc.document_type === 'cni' && <FaIdCard className="text-gray-600" />}
                          {doc.document_type === 'rib' && <FaCreditCard className="text-gray-600" />}
                          {!['kbis', 'cni', 'rib'].includes(doc.document_type) && <FaFileAlt className="text-gray-600" />}
                        </div>
                        <div>
                          <p className="font-medium">{doc.user?.name || 'Utilisateur'}</p>
                          <p className="text-sm text-gray-500">{doc.user?.email}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            {getDocumentTypeLabel(doc.document_type)} • {new Date(doc.uploaded_at).toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(`${API.replace('/api', '')}${doc.file_url}`, '_blank')}
                        >
                          <FaEye className="mr-1" /> Voir
                        </Button>
                        <Button
                          size="sm"
                          className="bg-green-500 hover:bg-green-600"
                          onClick={() => handleVerifyDocument(doc.id, 'verified')}
                        >
                          <FaCheck className="mr-1" /> Valider
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            setSelectedDoc(doc);
                            setVerificationAction('reject');
                          }}
                        >
                          <FaTimes className="mr-1" /> Rejeter
                        </Button>
                      </div>
                    </div>
                  ))}
                  {pendingDocs.length === 0 && (
                    <p className="text-gray-500 text-center py-8">Aucun document en attente de vérification</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Risk Tab */}
          <TabsContent value="risk">
            <Card>
              <CardContent className="p-6">
                <div className="space-y-4">
                  {highRiskUsers.map((risk) => (
                    <div key={risk.id} className="p-4 bg-white border rounded-lg">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <p className="font-medium text-lg">{risk.user?.name || 'Utilisateur'}</p>
                          <p className="text-sm text-gray-500">{risk.user?.email}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="text-3xl font-bold text-red-500">{risk.score}</p>
                            <p className="text-xs text-gray-500">Score de risque</p>
                          </div>
                          {getRiskLevelBadge(risk.score)}
                        </div>
                      </div>
                      
                      {/* Risk Signals */}
                      <div className="mt-4">
                        <p className="text-sm font-medium text-gray-700 mb-2">Signaux détectés :</p>
                        <div className="flex flex-wrap gap-2">
                          {risk.signals?.filter(s => !s.is_positive).map((signal, idx) => (
                            <Badge key={idx} variant="outline" className="text-red-600 border-red-200">
                              {signal.description} (+{signal.points})
                            </Badge>
                          ))}
                          {risk.signals?.filter(s => s.is_positive).map((signal, idx) => (
                            <Badge key={idx} variant="outline" className="text-green-600 border-green-200">
                              {signal.description} ({signal.points})
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                  {highRiskUsers.length === 0 && (
                    <p className="text-gray-500 text-center py-8">Aucun utilisateur à risque élevé</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports">
            <Card>
              <CardContent className="p-6">
                <div className="space-y-4">
                  {fraudReports.map((report) => (
                    <div key={report.id} className="p-4 bg-white border rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <Badge className={
                          report.report_type === 'fraud' ? 'bg-red-500' :
                          report.report_type === 'theft' ? 'bg-orange-500' :
                          report.report_type === 'non_payment' ? 'bg-yellow-500' :
                          'bg-purple-500'
                        }>
                          {report.report_type === 'fraud' ? 'Fraude' :
                           report.report_type === 'theft' ? 'Vol' :
                           report.report_type === 'non_payment' ? 'Impayé' :
                           'Usurpation'}
                        </Badge>
                        <span className="text-sm text-gray-500">
                          {new Date(report.created_at).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                      
                      <p className="text-gray-700 mb-3">{report.description}</p>
                      
                      <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-4">
                        {report.reported_email && <span>Email: {report.reported_email}</span>}
                        {report.reported_phone && <span>Tél: {report.reported_phone}</span>}
                        {report.reported_company_siren && <span>SIREN: {report.reported_company_siren}</span>}
                        {report.police_report_number && <span>N° plainte: {report.police_report_number}</span>}
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="bg-red-500 hover:bg-red-600"
                          onClick={() => handleReviewFraudReport(report.id, 'confirmed')}
                        >
                          Confirmer le signalement
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleReviewFraudReport(report.id, 'dismissed')}
                        >
                          Rejeter
                        </Button>
                      </div>
                    </div>
                  ))}
                  {fraudReports.length === 0 && (
                    <p className="text-gray-500 text-center py-8">Aucun signalement en attente</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Rejection Modal */}
        {selectedDoc && verificationAction === 'reject' && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md mx-4">
              <CardHeader>
                <CardTitle>Rejeter le document</CardTitle>
                <CardDescription>
                  Indiquez la raison du rejet pour {selectedDoc.user?.name}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label>Raison du rejet</Label>
                    <Input
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="Ex: Document illisible, date expirée..."
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSelectedDoc(null);
                        setVerificationAction('');
                        setRejectionReason('');
                      }}
                    >
                      Annuler
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => handleVerifyDocument(selectedDoc.id, 'rejected')}
                      disabled={!rejectionReason}
                    >
                      Confirmer le rejet
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
